import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Chip
} from '@mui/material';
import { FaTimes, FaSearch, FaFileAlt, FaSpinner } from 'react-icons/fa';
import { Add as AddIcon, Close as CloseIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { uploadDocument, getDocuments, getDocument } from '../../../services/documents';
import './KnowledgeHub.css';
import { useUsage } from '../../../hooks/useUsage';

const KnowledgeHub = () => {
  const { user } = useAuth();
  const { showError, showSuccess, showWarning } = useToast();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openUpload, setOpenUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    category: '',
    tags: ''
  });
  const [uploadLoading, setUploadLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const searchTimeout = useRef();
  const [overlayDoc, setOverlayDoc] = useState(null);
  const [overlayLoading, setOverlayLoading] = useState(false);

  // Add usage tracking
  const { checkUsage, incrementUsage } = useUsage();

  const fetchDocuments = useCallback(async (searchTerm = '', category = categoryFilter) => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (category && category !== 'All') params.category = category;
      const response = await getDocuments(params);
      setDocuments(response.documents || []);
    } catch (err) {
      if (err.message && !err.message.includes('empty')) {
        showError(err.message || 'Failed to fetch documents');
      }
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, showError]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Fetch documents with search
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchDocuments(search, categoryFilter);
    }, 350);
    return () => clearTimeout(searchTimeout.current);
  }, [search, categoryFilter, fetchDocuments]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadFile(file);
      // Set title from filename if not already set
      if (!uploadData.title) {
        setUploadData(prev => ({
          ...prev,
          title: file.name.replace(/\.[^/.]+$/, '')
        }));
      }
    }
  };

  const handleUploadChange = (field) => (event) => {
    setUploadData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleUploadSubmit = async (event) => {
    event.preventDefault();
    if (!uploadFile) {
      showError('Please select a file to upload');
      return;
    }

    // Set loading state immediately for better UX
    setUploadLoading(true);

    try {
      // Check usage limits after setting loading state
      const usageCheck = await checkUsage('documentUploads');
      if (!usageCheck.allowed) {
        showWarning(`You've reached your monthly limit of ${usageCheck.limit} document uploads. Please upgrade your plan for unlimited access.`, {
          actions: [
            {
              label: 'Upgrade Plan',
              primary: true,
              onClick: () => window.location.href = '/billing'
            }
          ]
        });
        setUploadLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadData.title);
      formData.append('description', uploadData.description);
      formData.append('category', uploadData.category);
      formData.append('tags', uploadData.tags);
      formData.append('userId', user.id);

      await uploadDocument(formData);
      
      showSuccess('Document uploaded successfully!');
      
      // Reset form and close modal
      handleCloseUpload();
      fetchDocuments(); // Refresh the document list

    } catch (err) {
      console.error('Error uploading document:', err);
      showError(err.message || 'Failed to upload document');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleCloseUpload = () => {
    setOpenUpload(false);
    setUploadFile(null);
    setUploadData({
      title: '',
      description: '',
      category: '',
      tags: ''
    });
  };

  // Filtered docs
  const filteredDocs = documents.filter(doc => {
    const matchesSearch =
      doc.title.toLowerCase().includes(search.toLowerCase()) ||
      (doc.description && doc.description.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory = categoryFilter === 'All' || doc.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Category chips
  const categoryChips = ['All', 'Sales', 'Marketing', 'Product', 'Support', 'Other'];

  // Helper for date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return isNaN(date) ? '' : date.toLocaleDateString();
  };

  // Helper to highlight search term
  const highlightText = (text, term) => {
    if (!term) return text;
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.split(regex).map((part, i) =>
      regex.test(part) ? <mark key={i} style={{ background: '#ffe066', color: '#1e293b', padding: 0 }}>{part}</mark> : part
    );
  };

  const handleOpenOverlay = async (docId) => {
    setOverlayLoading(true);
    try {
      const doc = await getDocument(docId);
      setOverlayDoc(doc);
    } catch (error) {
      console.error('Error loading document:', error);
      // Ensure we're not accidentally throwing an Event object
      const errorMessage = error instanceof Event ? 'Failed to load document due to an unexpected event' : (error.message || 'Failed to load document.');
      showError(errorMessage);
      setOverlayDoc(null);
    } finally {
      setOverlayLoading(false);
    }
  };

  const handleCloseOverlay = () => setOverlayDoc(null);

  // Helper to check file type
  const isPdf = (filePath = '', fileType = '') => {
    const lowerFileType = fileType.toLowerCase();
    const lowerFilePath = filePath.toLowerCase();
    return lowerFileType === 'application/pdf' || lowerFileType === 'pdf' || lowerFilePath.endsWith('.pdf');
  };
  const isImage = (filePath = '', fileType = '') => /image\//.test(fileType) || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(filePath);

  return (
    <div style={{ className: 'knowledge-hub-layout', background: '#F7F9FC', minHeight: 'calc(100vh - 10px)', width: '100%', borderRadius: '10px 0px 0px 0px', marginTop: '10px', overflow: 'hidden'}}>
        <div className="knowledge-hub-header-row" style={{ background: 'linear-gradient(120deg, rgba(241, 245, 249, 1) 3%, rgba(219, 234, 254, 1) 37%, rgba(237, 233, 254, 1) 100%)', height: '9vh', padding: '10px 0px 10px 10px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', marginBottom: 24, alignItems: 'flex-start' }}>
          <div className="knowledge-hub-header-left">
            <div style={{ fontSize: 24, fontWeight: 600, padding: 0, margin: 0 }}>Knowledge Hub</div>
          </div>
          <div className="knowledge-hub-header-right" style={{ display: 'flex', gap: 8, paddingRight: 10 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenUpload(true)}
              style={{ 
                height: 30, 
                fontWeight: 600, 
                fontSize: 10, 
                padding: '0px 4px 0px 4px', 
                marginRight: 10,
                '& .MuiButton-startIcon': {
                  marginRight: 2
                }
              }}
              sx={{
                '& .MuiButton-startIcon': {
                  marginRight: 0.5
                }
              }}
            >
              Add Document
            </Button>
          </div>
        </div>
      <div className="knowledge-hub-container" style={{ background: 'transparent', marginTop: 0 }}>
        <div className="knowledge-hub-search-container" style={{ display: 'flex', gap: 16, margin: '24px 0 8px 0', alignItems: 'center', position: 'relative', width: '90%', marginLeft: '10px'}}>
          <span style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center'}}>
            <FaSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: 18, pointerEvents: 'none' }} />
            <input
              type="search"
              placeholder="Search documents by title, description, or tags..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, padding: '12px 36px 12px 40px', borderRadius: 8, border: '1px solid #2563eb', fontSize: 16, background: '#f', outline: 'none' }}
              autoComplete="off"
              aria-label="Search documents"
              onKeyDown={e => {
                if (e.key === 'Escape') setSearch('');
              }}
            />
            {loading && search && (
              <span style={{ position: 'absolute', right: 36, top: '50%', transform: 'translateY(-50%)', color: '#2563eb' }}>
                <FaSpinner style={{ animation: 'spin 0.7s linear infinite', fontSize: '18px', color: '#2563eb' }} />
              </span>
            )}
            {search && (
              <button
                onClick={() => setSearch('')}
                aria-label="Clear search"
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', fontSize: 18, cursor: 'pointer', padding: 0 }}
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setSearch(''); }}
              >
                <FaTimes />
              </button>
            )}
          </span>
          <div className="knowledge-hub-category-filters" style={{ display: 'flex', gap: 8 }}>
            {categoryChips.map(cat => (
              <Chip
                key={cat}
                label={cat}
                clickable
                color={categoryFilter === cat ? 'primary' : 'default'}
                onClick={() => setCategoryFilter(cat)}
                style={{ fontWeight: 500, fontSize: 15, borderRadius: 8 }}
              />
            ))}
          </div>
        </div>
        {loading ? (
          <Box className="loading-container">
            <FaSpinner />
          </Box>
        ) : filteredDocs.length === 0 ? (
          <Paper className="empty-state">
            <Typography variant="h6" color="textSecondary">
              No documents found
            </Typography>
            <Typography color="textSecondary">
              Click "Add Document" to upload your first document
            </Typography>
          </Paper>
        ) : (
          <div className="search-results" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 24,
            marginTop: 8,
            marginLeft: '10px',
            background: 'transparent',
            maxHeight: '70vh',
            overflowY: 'auto',
            paddingRight: 8,
            border: 'none',
            boxShadow: 'none'
          }}>
            {filteredDocs.map((doc) => (
              <div className="document-card" key={doc.id} style={{ 
                background: '#fff', 
                borderRadius: 12, 
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)', 
                padding: 24, 
                minWidth: 0, 
                maxWidth: 400, 
                minHeight: 200,
                display: 'flex', 
                flexDirection: 'column', 
                gap: 8, 
                border: '1px solid #e5e7eb', 
                position: 'relative',
                overflow: 'hidden'
              }}>
                <Chip className="document-category" label={doc.category} color="primary" size="small" style={{ position: 'absolute', top: 18, left: 18, fontWeight: 600, fontSize: 13, borderRadius: 6 }} />
                <div 
                  className="document-title" 
                  title={doc.title}
                  style={{
                    fontWeight: 700, 
                    fontSize: 18, 
                    marginTop: 20, 
                    marginBottom: 4,
                    lineHeight: '1.3',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    wordBreak: 'break-word',
                    maxHeight: 'calc(1.3em * 3)',
                    cursor: 'default'
                  }}
                >{highlightText(doc.title, search)}</div>
                <div style={{ 
                  color: '#64748b', 
                  fontSize: 14, 
                  marginTop: 'auto',
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 4,
                  alignItems: 'flex-end'
                }}>
                  Added {formatDate(doc.createdat) || ''}
                  {doc.tags && Array.isArray(doc.tags) && doc.tags.length > 0 && (
                    <span style={{ marginLeft: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {doc.tags.map((tag, i) => (
                        <span key={i} style={{ 
                          background: '#e3f2fd', 
                          color: '#1976d2', 
                          borderRadius: 4, 
                          padding: '2px 6px', 
                          fontSize: 12, 
                          fontWeight: 500,
                          marginBottom: 2
                        }}>
                          {highlightText(tag, search)}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
                <FaFileAlt
                  style={{ position: 'absolute', top: 18, right: 18, color: '#A9A9A9', fontSize: 22, cursor: 'pointer' }}
                  title="View Document"
                  onClick={() => handleOpenOverlay(doc.id)}
                />
              </div>
            ))}
          </div>
        )}

        {/* Upload Document Dialog */}
        <Dialog
          open={openUpload}
          onClose={handleCloseUpload}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Upload Document
            <IconButton
              aria-label="close"
              onClick={handleCloseUpload}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <form onSubmit={handleUploadSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ marginTop: 5, height: 100, borderStyle: 'dashed' }}
                  >
                    {uploadFile ? uploadFile.name : 'Click to select a file'}
                    <input
                      type="file"
                      hidden
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx,.txt,.csv,.xlsx"
                    />
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={uploadData.title}
                    onChange={handleUploadChange('title')}
                    required
                    disabled={uploadLoading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={uploadData.description}
                    onChange={handleUploadChange('description')}
                    multiline
                    rows={3}
                    disabled={uploadLoading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth required disabled={uploadLoading}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={uploadData.category}
                      label="Category"
                      onChange={handleUploadChange('category')}
                    >
                      <MenuItem value="Sales">Sales</MenuItem>
                      <MenuItem value="Marketing">Marketing</MenuItem>
                      <MenuItem value="Product">Product</MenuItem>
                      <MenuItem value="Support">Support</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tags (comma-separated)"
                    value={uploadData.tags}
                    onChange={handleUploadChange('tags')}
                    placeholder="e.g. sales, marketing, product"
                    disabled={uploadLoading}
                    helperText="Enter tags separated by commas"
                  />
                </Grid>
              </Grid>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUpload} disabled={uploadLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleUploadSubmit}
              variant="contained"
              disabled={uploadLoading || !uploadFile}
              startIcon={uploadLoading ? <FaSpinner size={20} /> : null}
            >
              {uploadLoading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Overlay for document view */}
        {(overlayDoc || overlayLoading) && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleCloseOverlay}>
            <div style={{ background: '#fff', borderRadius: 12, maxWidth: 900, width: '95vw', maxHeight: '90vh', overflowY: 'auto', padding: 32, position: 'relative', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
              <button onClick={handleCloseOverlay} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 24, color: '#64748b', cursor: 'pointer' }} aria-label="Close">×</button>
              {overlayLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <FaSpinner style={{ marginBottom: 16 }} />
                  <div style={{ color: '#64748b', fontSize: 18, marginTop: 16 }}>Loading document...</div>
                </div>
              ) : overlayDoc ? (
                <>
                  <h2 style={{ margin: 0, fontSize: 28, marginBottom: 16 }}>{overlayDoc.title}</h2>
                  {(overlayDoc.signedUrl || overlayDoc.filepath) && (
                    (() => {
                      const fileUrl = overlayDoc.signedUrl || overlayDoc.filepath;
                      const fallbackUrl = overlayDoc.filepath; // Original URL as fallback
                      const fileType = overlayDoc.fileType || overlayDoc.filetype || '';
                      const isPdfFile = isPdf(fileUrl, fileType);
                      const isImageFile = isImage(fileUrl, fileType);
                      
                      console.log('Document overlay debug:', {
                        title: overlayDoc.title,
                        fileUrl,
                        fallbackUrl,
                        fileType,
                        isPdfFile,
                        isImageFile,
                        signedUrl: overlayDoc.signedUrl,
                        filepath: overlayDoc.filepath,
                        fileTypeFromBackend: overlayDoc.fileType,
                        filetypeFromBackend: overlayDoc.filetype
                      });
                      
                      if (isPdfFile) {
                        return (
                          <div>
                            <iframe
                              src={fileUrl}
                              title="Document PDF"
                              style={{ width: '100%', height: '80vh', border: '1px solid #e5e7eb', borderRadius: 8 }}
                              frameBorder={0}
                              allowFullScreen
                              onError={(e) => {
                                console.error('Error loading PDF in iframe:', e);
                              }}
                            />
                            {fallbackUrl && fallbackUrl !== fileUrl && (
                              <div style={{ marginTop: 16, textAlign: 'center' }}>
                                <a 
                                  href={fallbackUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  style={{ 
                                    color: '#2563eb', 
                                    fontWeight: 600, 
                                    fontSize: 14, 
                                    textDecoration: 'underline'
                                  }}
                                >
                                  Open in new tab if PDF doesn't load
                                </a>
                              </div>
                            )}
                          </div>
                        );
                      } else if (isImageFile) {
                        return (
                          <img
                            src={fileUrl}
                            alt={overlayDoc.title}
                            style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8, border: '1px solid #e5e7eb' }}
                          />
                        );
                      } else {
                        return (
                          <div style={{ textAlign: 'center', padding: 40 }}>
                            <div style={{ marginBottom: 16, fontSize: 18, color: '#64748b' }}>
                              Document ready for download
                            </div>
                            <a 
                              href={fileUrl} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              style={{ 
                                color: '#2563eb', 
                                fontWeight: 600, 
                                fontSize: 18, 
                                textDecoration: 'underline',
                                padding: '12px 24px',
                                border: '2px solid #2563eb',
                                borderRadius: 8,
                                display: 'inline-block'
                              }}
                            >
                              Open Document
                            </a>
                          </div>
                        );
                      }
                    })()
                  )}
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeHub; 